import { Fragment, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isCodeNode } from "@lexical/code";
import { $isLinkNode } from "@lexical/link";
import { $isListNode } from "@lexical/list";
import { $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  KEY_MODIFIER_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";

import { TOOLBAR_GROUPS } from "../toolbar/controls";
import { BEHAVIOR_BY_KIND } from "../toolbar/controlBehavior";
import {
  ariaKeyshortcuts,
  formatShortcut,
  matchesShortcut,
} from "../toolbar/shortcut";
import CategoryMenu from "../toolbar/CategoryMenu";
import TemplateMenu from "../toolbar/TemplateMenu";
import ToolbarToggle from "../toolbar/ToolbarToggle";
import LinkDialogPlugin from "./LinkDialogPlugin";
import { Toolbar, ToolbarSeparator } from "@/components/ui/toolbar";

const EMPTY_STATE = {
  formats: {},
  blockType: "paragraph",
  alignment: "",
  isLink: false,
};

// Controls whose keybinding this plugin registers itself. The rest carry a
// `shortcut` for display only, because Lexical or LinkDialogPlugin bind them.
const BINDABLE_CONTROLS = TOOLBAR_GROUPS.flat().filter(
  control => control.bindShortcut
);

function $readToolbarState() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const anchorNode = selection.anchor.getNode();
  // Null only when the anchor is the root itself, which is already an element.
  const element = anchorNode.getTopLevelElement() ?? anchorNode;

  let blockType = "paragraph";
  if ($isListNode(element)) {
    blockType = element.getListType();
  } else if ($isHeadingNode(element)) {
    blockType = element.getTag();
  } else if ($isQuoteNode(element)) {
    blockType = "quote";
  } else if ($isCodeNode(element)) {
    blockType = "code";
  }

  return {
    formats: {
      bold: selection.hasFormat("bold"),
      italic: selection.hasFormat("italic"),
      underline: selection.hasFormat("underline"),
      strikethrough: selection.hasFormat("strikethrough"),
      code: selection.hasFormat("code"),
    },
    blockType,
    alignment: $isElementNode(element) ? element.getFormatType() : "",
    isLink: $findMatchingParent(anchorNode, $isLinkNode) !== null,
  };
}

function ToolbarPlugin({
  onAttach,
  templates,
  onSelectTemplate,
  categories,
  categoryValue,
  onChangeCategory,
  onManageCategories,
  isFullscreen,
  onToggleFullscreen,
}) {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState(EMPTY_STATE);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const refresh = useCallback(() => {
    const next = $readToolbarState();
    if (next) {
      setState(next);
    }
  }, []);

  const setBlock = useCallback(
    createNode => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, createNode);
        }
      });
    },
    [editor]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) =>
          editorState.read(refresh)
        ),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            refresh();
            return false;
          },
          COMMAND_PRIORITY_CRITICAL
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          payload => {
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          payload => {
            setCanRedo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL
        ),
        // The keyboard and click paths share controlBehavior, so heading and
        // list toggle semantics stay identical. State is read fresh here rather
        // than closed over, which keeps this effect off the render state.
        editor.registerCommand(
          KEY_MODIFIER_COMMAND,
          event => {
            const control = BINDABLE_CONTROLS.find(candidate =>
              matchesShortcut(event, candidate.shortcut)
            );
            if (!control) {
              return false;
            }
            event.preventDefault();
            const state = editor.read($readToolbarState) ?? EMPTY_STATE;
            const behavior = BEHAVIOR_BY_KIND[control.kind];
            const active = behavior.isActive({ control, state });
            behavior.run({ control, active, state, editor, setBlock });
            return true;
          },
          COMMAND_PRIORITY_NORMAL
        )
      ),
    [editor, refresh, setBlock]
  );

  const buildContext = control => {
    const active = BEHAVIOR_BY_KIND[control.kind]?.isActive?.({
      control,
      state,
      isFullscreen,
    });
    return {
      control,
      active,
      state,
      canUndo,
      canRedo,
      editor,
      setBlock,
      openLinkDialog: () => setLinkDialogOpen(true),
      onAttach,
      onSelectTemplate,
      onChangeCategory,
      onManageCategories,
      onToggleFullscreen,
    };
  };

  return (
    <>
      <Toolbar aria-label="Formatting">
        {TOOLBAR_GROUPS.map((group, index) => (
          <Fragment key={group[0].id}>
            {index > 0 && <ToolbarSeparator />}
            {group.map(control => {
              const behavior = BEHAVIOR_BY_KIND[control.kind];
              const ctx = buildContext(control);
              const disabled = behavior?.isDisabled?.(ctx) ?? false;
              // Hidden, not rendered disabled, where a note-only control's
              // handler is absent.
              if (control.noteOnly && disabled) {
                return null;
              }
              if (control.kind === "template" && onSelectTemplate) {
                return (
                  <TemplateMenu
                    key={control.id}
                    templates={templates}
                    onSelect={onSelectTemplate}
                  />
                );
              }
              if (control.kind === "category" && onChangeCategory) {
                return (
                  <CategoryMenu
                    key={control.id}
                    categories={categories}
                    value={categoryValue}
                    onChange={onChangeCategory}
                  />
                );
              }
              return (
                <ToolbarToggle
                  key={control.id}
                  label={control.label}
                  tooltip={ctx.active ? control.activeTooltip : undefined}
                  icon={
                    ctx.active && control.activeIcon
                      ? control.activeIcon
                      : control.icon
                  }
                  active={ctx.active}
                  disabled={disabled}
                  shortcut={
                    control.shortcut && formatShortcut(control.shortcut)
                  }
                  shortcutKeys={
                    control.shortcut && ariaKeyshortcuts(control.shortcut)
                  }
                  onSelect={() => behavior?.run?.(ctx)}
                />
              );
            })}
          </Fragment>
        ))}
      </Toolbar>
      <LinkDialogPlugin
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
    </>
  );
}

ToolbarPlugin.propTypes = {
  onAttach: PropTypes.func,
  templates: PropTypes.arrayOf(PropTypes.object),
  onSelectTemplate: PropTypes.func,
  categories: PropTypes.arrayOf(PropTypes.object),
  categoryValue: PropTypes.number,
  onChangeCategory: PropTypes.func,
  onManageCategories: PropTypes.func,
  isFullscreen: PropTypes.bool,
  onToggleFullscreen: PropTypes.func,
};

export default ToolbarPlugin;
